// CSS-token / arbitrary-color-string resolution for pattern palettes.
//
// The renderers operate on hex strings (so the existing `hexToRgb` pipeline
// is unchanged). Consumers that want palettes derived from design tokens
// (e.g. Morfi's `--outcome-blue-500`) call these helpers to resolve token
// names into concrete hex values *at the moment of selection*, then pass
// the resulting `Palette` to the renderer like any other palette.
//
// The resolution path uses a 1×1 offscreen canvas to convert any valid CSS
// color (oklch, rgb, hsl, named, hex) to RGB, which we then format as hex.
// This means whatever the browser's painting engine accepts as a color, we
// can normalize. Required because Morfi's tokens resolve to `oklch(...)`,
// which the canvas API understands directly but our hex-keyed palette
// does not.
//
// Browser-only — `document` must be available. SSR returns `null`.

import type { Hex, Palette } from '../../types';

let cachedCtx: CanvasRenderingContext2D | null | undefined;

function getResolverCtx(): CanvasRenderingContext2D | null {
  if (cachedCtx !== undefined) return cachedCtx;
  if (typeof document === 'undefined') {
    cachedCtx = null;
    return null;
  }
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  cachedCtx = canvas.getContext('2d');
  return cachedCtx;
}

function rgbToHex(r: number, g: number, b: number): Hex {
  const hex = (v: number): string => v.toString(16).padStart(2, '0');
  return `#${hex(r)}${hex(g)}${hex(b)}`.toUpperCase();
}

/**
 * Resolve any CSS color string (`#abc`, `rgb(…)`, `hsl(…)`, `oklch(…)`,
 * named colors) to a uppercase 7-character hex string. Returns `null` in
 * SSR or when the canvas API is unavailable.
 *
 * Implementation note: an invalid CSS color leaves `fillStyle` unchanged,
 * so we reset to `#000000` first. If the input was invalid the function
 * returns `'#000000'` (caller can treat that as suspect if it matters).
 */
export function resolveCssColor(css: string): Hex | null {
  const ctx = getResolverCtx();
  if (ctx === null) return null;
  const trimmed = css.trim();
  if (trimmed === '') return null;
  ctx.clearRect(0, 0, 1, 1);
  ctx.fillStyle = '#000000';
  ctx.fillStyle = trimmed;
  ctx.fillRect(0, 0, 1, 1);
  const data = ctx.getImageData(0, 0, 1, 1).data;
  const r = data[0] ?? 0;
  const g = data[1] ?? 0;
  const b = data[2] ?? 0;
  return rgbToHex(r, g, b);
}

/**
 * Resolve a CSS custom property name (with or without the leading `--`)
 * read from `:root` to a hex string. Returns `null` if the variable is
 * undefined or unresolvable.
 *
 * The value reflects the *current* mode (light/dark) — call again after
 * a theme toggle if you want the new mode's resolution.
 *
 * @example
 *   resolveColorToken('--outcome-blue-500')   // '#0E5BC9' (or whatever)
 *   resolveColorToken('outcome-blue-500')      // same — leading '--' optional
 */
export function resolveColorToken(token: string): Hex | null {
  if (typeof document === 'undefined') return null;
  const varName = token.startsWith('--') ? token : `--${token}`;
  const value = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
  if (value === '') return null;
  return resolveCssColor(value);
}

/**
 * Bulk variant of `resolveColorToken`. Skips tokens that fail to resolve
 * (so the returned palette may be shorter than `tokens` if some tokens
 * are missing — the caller should validate length if that matters).
 */
export function resolvePaletteFromTokens(tokens: readonly string[]): Palette {
  const out: Hex[] = [];
  for (const token of tokens) {
    const hex = resolveColorToken(token);
    if (hex !== null) out.push(hex);
  }
  return out;
}
